import useSWR from 'swr';
import { getIndexUsage } from '../../api';
import type { IndexUsageResponse, GetIndexUsageParams } from './types/database';

interface UseIndexUsageReturn {
  data: IndexUsageResponse | undefined;
  isLoading: boolean;
  isError: any;
  mutate: () => void;
}

/**
 * Hook to fetch index usage statistics from pg_stat_user_indexes
 *
 * @param params - Query parameters (table_name, min_size_mb, unused_only, limit)
 * @returns Index usage data, loading state, error state, and mutate function
 *
 * @example
 * const { data, isLoading } = useIndexUsage({ unused_only: true, min_size_mb: 1 });
 */
export function useIndexUsage(params?: GetIndexUsageParams): UseIndexUsageReturn {
  const key = params
    ? `/admin/db-stats/index-usage?${new URLSearchParams(Object.entries(params).map(([k, v]) => [k, String(v)])).toString()}`
    : '/admin/db-stats/index-usage';

  const { data, error, mutate } = useSWR<IndexUsageResponse>(
    key,
    () => getIndexUsage(params),
    {
      refreshInterval: 300000, // Refresh every 5 minutes (less frequent than slow queries)
      revalidateOnMount: true,
    }
  );

  return {
    data,
    isLoading: !error && !data,
    isError: error,
    mutate,
  };
}
